// Row actions on a time entry (coverage rows 66, 67; managers): edit the minutes or note (a new
// revision, `PATCH /time-entries/{id}`), approve or reject (with a reason), and void
// (`DELETE /time-entries/{id}`, behind an AlertDialog). Used by the DE profile's Time tab and the
// hours entry screen. The server decides who may approve: a decision on one's own time is
// refused (403), and the refusal is shown in place, never hidden behind a guess.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Textarea,
  toast,
} from "@companyos/ui";
import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { BanIcon, MoreHorizontalIcon, PencilIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { TimeEntry } from "../../api/types";
import { formatDate, formatMinutes, TIME_KIND_LABELS } from "../format";
import { RowAction, RowActions } from "../settings/row-actions";
import { problemText } from "./de-status";

type Open = "edit" | "reject" | "void" | null;

/** Every time list and seat cost of the company: a change moves totals and human-time cost. */
export function refreshTime(qc: QueryClient, companyId: string) {
  return qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "company" &&
      q.queryKey[1] === companyId &&
      (q.queryKey.includes("time") || q.queryKey.includes("cost")),
  });
}

export function TimeEntryActions({
  api,
  companyId,
  entry,
  who,
}: {
  api: Api;
  companyId: string;
  entry: TimeEntry;
  /** The person's name, for the dialogs' wording. */
  who: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<Open>(null);
  const [minutes, setMinutes] = useState(String(entry.minutes));
  const [note, setNote] = useState(entry.note ?? "");
  const [reason, setReason] = useState("");
  const ids = { minutes: useId(), note: useId(), reason: useId() };
  const what = `${formatMinutes(entry.minutes)} of ${TIME_KIND_LABELS[entry.kind]?.toLowerCase() ?? entry.kind} on ${formatDate(entry.work_date)}`;
  const done = (title: string) => {
    void refreshTime(qc, companyId);
    setOpen(null);
    toast.add({ title, description: `${who}: ${what}` });
  };

  const patch = useMutation({
    mutationFn: (body: Parameters<Api["client"]["timeEntries"]["update"]>[0]["body"]) =>
      api.client.timeEntries.update({ params: { co: companyId, id: entry.id }, body }),
  });
  const remove = useMutation({
    mutationFn: () => api.client.timeEntries.delete({ params: { co: companyId, id: entry.id } }),
    onSuccess: () => done("Entry voided"),
  });
  const value = Number(minutes);
  const editValid = Number.isInteger(value) && value > 0;
  const edited = value !== entry.minutes || note.trim() !== (entry.note ?? "");

  const openDialog = (which: Open) => {
    patch.reset();
    remove.reset();
    setMinutes(String(entry.minutes));
    setNote(entry.note ?? "");
    setReason("");
    setOpen(which);
  };
  const error = patch.isError ? (
    <p role="alert" className="text-sm text-danger-text">
      {problemText(patch.error, "The entry could not be changed.")}
    </p>
  ) : null;

  return (
    <span className="inline-flex items-center justify-end gap-0.5">
      {/* Revise and void as hover icons (the row carries `group/row`); the menu keeps all four. */}
      <RowActions>
        <RowAction
          icon={PencilIcon}
          label={`Revise ${who}, ${what}`}
          tip="Revise"
          onClick={() => openDialog("edit")}
        />
        <RowAction
          icon={BanIcon}
          label={`Void ${who}, ${what}`}
          tip="Void"
          className="hover:text-danger-text"
          onClick={() => openDialog("void")}
        />
      </RowActions>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Actions for ${who}, ${what}`}
              data-slot="time-entry-actions"
            />
          }
        >
          <MoreHorizontalIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-44">
          <DropdownMenuItem onClick={() => openDialog("edit")}>Edit minutes</DropdownMenuItem>
          {entry.approval_status !== "approved" ? (
            <DropdownMenuItem
              onClick={() =>
                patch.mutate(
                  { approval_status: "approved" },
                  {
                    onSuccess: () => done("Entry approved"),
                    // Shown as a toast by the query client; nothing else to do here.
                  },
                )
              }
            >
              Approve
            </DropdownMenuItem>
          ) : null}
          {entry.approval_status !== "rejected" ? (
            <DropdownMenuItem onClick={() => openDialog("reject")}>Reject…</DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={() => openDialog("void")}>
            Void…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={open === "edit" || open === "reject"}
        onOpenChange={(next) => {
          if (!next && !patch.isPending) setOpen(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          {open === "edit" ? (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (editValid && edited)
                  patch.mutate(
                    {
                      ...(value !== entry.minutes ? { duration_seconds: value * 60 } : {}),
                      ...(note.trim() !== (entry.note ?? "") ? { note: note.trim() || null } : {}),
                    },
                    { onSuccess: () => done("Entry revised") },
                  );
              }}
            >
              <DialogHeader>
                <DialogTitle>Edit time entry</DialogTitle>
                <DialogDescription>
                  {who}, {what}. The change is kept as a new revision; a changed duration needs
                  approval again unless you may approve it.
                </DialogDescription>
              </DialogHeader>
              <Field>
                <FieldLabel htmlFor={ids.minutes}>Minutes</FieldLabel>
                <Input
                  id={ids.minutes}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  required
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.note}>Note</FieldLabel>
                <Input id={ids.note} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              {error}
              <DialogFooter>
                <DialogClose
                  disabled={patch.isPending}
                  render={<Button variant="secondary" type="button" />}
                >
                  Cancel
                </DialogClose>
                <Button type="submit" disabled={!editValid || !edited || patch.isPending}>
                  {patch.isPending ? "Saving…" : "Save revision"}
                </Button>
              </DialogFooter>
            </form>
          ) : open === "reject" ? (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                patch.mutate(
                  {
                    approval_status: "rejected",
                    ...(reason.trim() ? { rejection_reason: reason.trim() } : {}),
                  },
                  { onSuccess: () => done("Entry rejected") },
                );
              }}
            >
              <DialogHeader>
                <DialogTitle>Reject time entry</DialogTitle>
                <DialogDescription>
                  {who}, {what}. A rejected entry stays on record and counts nowhere.
                </DialogDescription>
              </DialogHeader>
              <Field>
                <FieldLabel htmlFor={ids.reason}>Reason</FieldLabel>
                <Textarea
                  id={ids.reason}
                  value={reason}
                  maxLength={2000}
                  onChange={(e) => setReason(e.target.value)}
                />
                <FieldDescription>Optional. Shown with the entry.</FieldDescription>
              </Field>
              {error}
              <DialogFooter>
                <DialogClose
                  disabled={patch.isPending}
                  render={<Button variant="secondary" type="button" />}
                >
                  Cancel
                </DialogClose>
                <Button type="submit" variant="destructive" disabled={patch.isPending}>
                  {patch.isPending ? "Rejecting…" : "Reject entry"}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={open === "void"}
        onOpenChange={(next) => {
          if (!next && !remove.isPending) setOpen(null);
        }}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Void this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {who}, {what}. A voided entry leaves the totals and its human-time cost is reversed.
              It stays in the audit trail and cannot be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {remove.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(remove.error, "The entry could not be voided.")}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? "Voiding…" : "Void entry"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
}
